import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Card, OverlayTrigger, Tooltip, Row, Col, Spinner, Container, FormGroup } from 'react-bootstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { isEmpty, cloneDeep } from 'lodash';
import Loader from '../../Components/Utility/Loader/Loader';
import {
  SelectBoxStylesForProductAttribute,
  createOptions,
  getYears,
  checkIsNumber,
  checkArray,
  convertSelectLabel,
  currencyCodes,
} from '../../Components/helper';
import { getSeasonsDetails, fetchExchangeRate, saveExhangeRate } from './Store/SystemMasters.action';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { CurrentYearOption } from '../../Components/Utility/CurrentYearDropdown';

const EMPTY_COMBINATION = () => ({ currency: null, value: '' });

function ExchangeRate() {
  const dispatch = useDispatch();
  const reducer = useSelector((estate) => estate);
  const {
    SystemMastersDetails,
    UserDetails: { userInfo },
    Users: { logInRights },
  } = reducer;
  const { CheckActionPermission } = useComponent();
  const { t } = useTranslation(['common']);
  const { t: sMaster } = useTranslation(['systemMaster']);

  const [userRights, setUserRights] = useState({});

  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);

  const MasterAddEditAccess = CheckActionPermission(userRights, 'masters', 'exchangeRates', 'addEdit');
  const { seasonList, exchangeRateData } = SystemMastersDetails;
  const loggedInUserId = userInfo?.userId;

  const [state, setState] = useState({
    buttonDownCustomAttr: true,
    loader: false,
    exchangeRateList: [],
    selectedDefaultCurrency: null,
    seasonId: null,
    year: null,
    yearOpionsArr: createOptions(getYears(), 'name', '_id'),
    currencyOptionsArr: currencyCodes.map((currKey) => convertSelectLabel(currKey, true)),
    isSaved: false,
    error: {},
    exchangeRateId: null,
  });

  const {
    buttonDownCustomAttr,
    loader,
    exchangeRateList,
    selectedDefaultCurrency,
    seasonId,
    year,
    yearOpionsArr,
    currencyOptionsArr,
    isSaved,
    error,
    exchangeRateId,
  } = state;

  const activeItem = exchangeRateList.find((item) => item.defaultCurrency?.value === selectedDefaultCurrency?.value) || {
    defaultCurrency: selectedDefaultCurrency,
    defaultCurrencyValue: '1',
    combinationCurrencies: [EMPTY_COMBINATION()],
  };

  const renderTooltipOpenCloseCustomAttr = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      {!buttonDownCustomAttr ? t('EXCHANGE_RATE.TOOLTIP_CLOSE') : t('EXCHANGE_RATE.TOOLTIP_OPEN')}
    </Tooltip>
  );

  useEffect(() => {
    const loadedList = checkArray(exchangeRateData?.exchangeRateList)
      ? exchangeRateData.exchangeRateList.map((item) => ({
          defaultCurrency: item.defaultCurrency ? convertSelectLabel(item.defaultCurrency, true) : null,
          defaultCurrencyValue: item.defaultCurrencyValue || '1',
          combinationCurrencies: checkArray(item.combinationCurrencies)
            ? item.combinationCurrencies.map((rate) => ({
                currency: convertSelectLabel(rate.currency, true),
                value: rate.value,
              }))
            : [EMPTY_COMBINATION()],
        }))
      : [];

    setState((prevState) => {
      const previousSelection = prevState.selectedDefaultCurrency;
      const stillExists =
        previousSelection && loadedList.some((item) => item.defaultCurrency?.value === previousSelection.value);
      const nextSelectedDefaultCurrency = stillExists
        ? previousSelection
        : loadedList.length > 0
          ? loadedList[0].defaultCurrency
          : null;

      return {
        ...prevState,
        exchangeRateList: loadedList,
        selectedDefaultCurrency: nextSelectedDefaultCurrency,
        exchangeRateId: exchangeRateData?._id || null,
      };
    });
  }, [exchangeRateData]);

  const toggleComp = async () => {
    if (buttonDownCustomAttr && isEmpty(seasonList)) {
      dispatch(getSeasonsDetails({ loggedInUserId }));
    }
    setState((prevState) => ({
      ...prevState,
      buttonDownCustomAttr: !prevState.buttonDownCustomAttr,
    }));
  };

  const btnCall = () => {
    if (buttonDownCustomAttr === false) {
      return <i className="icon-new-up" />;
    }
    return <i className="icon-new-down" />;
  };

  const handleFetchApiCall = (copyState) => {
    if (!isEmpty(copyState?.seasonId) && !isEmpty(copyState?.year)) {
      dispatch(
        fetchExchangeRate({
          seasonId: copyState?.seasonId?._id,
          year: copyState?.year?.name,
        }),
      );
    }
  };

  const handleSeasonChange = (value) => {
    const next = { ...state, seasonId: value, error: { ...state.error, seasonId: '' } };
    setState(next);
    handleFetchApiCall(next);
  };

  const handleYearChange = (value) => {
    const next = { ...state, year: value, error: { ...state.error, year: '' } };
    setState(next);
    handleFetchApiCall(next);
  };

  /** Switch the active defaultCurrency; load saved combinations or start fresh. */
  const handleDefaultCurrency = (value) => {
    const copy = cloneDeep(exchangeRateList);
    const existingIdx = copy.findIndex((item) => item.defaultCurrency?.value === value?.value);

    if (existingIdx === -1) {
      const savedEntry = exchangeRateData?.exchangeRateList?.find((item) => item.defaultCurrency === value?.value);
      if (savedEntry && checkArray(savedEntry.combinationCurrencies)) {
        copy.push({
          defaultCurrency: value,
          defaultCurrencyValue: savedEntry.defaultCurrencyValue || '1',
          combinationCurrencies: savedEntry.combinationCurrencies.map((rate) => ({
            currency: convertSelectLabel(rate.currency, true),
            value: rate.value,
          })),
        });
      } else {
        copy.push({ defaultCurrency: value, defaultCurrencyValue: '1', combinationCurrencies: [EMPTY_COMBINATION()] });
      }
    }

    setState((prevState) => ({ ...prevState, exchangeRateList: copy, selectedDefaultCurrency: value }));
  };

  const addCombinationCurrency = () => {
    const copy = cloneDeep(exchangeRateList);
    const idx = copy.findIndex((item) => item.defaultCurrency?.value === selectedDefaultCurrency?.value);
    if (idx !== -1) {
      copy[idx].combinationCurrencies.push(EMPTY_COMBINATION());
    } else {
      copy.push({ defaultCurrency: selectedDefaultCurrency, defaultCurrencyValue: '1', combinationCurrencies: [EMPTY_COMBINATION()] });
    }
    setState((prevState) => ({ ...prevState, exchangeRateList: copy }));
  };

  const removeCombinationCurrency = (innerIdx) => {
    const copy = cloneDeep(exchangeRateList);
    const idx = copy.findIndex((item) => item.defaultCurrency?.value === selectedDefaultCurrency?.value);
    if (idx !== -1) copy[idx].combinationCurrencies.splice(innerIdx, 1);
    setState((prevState) => ({ ...prevState, exchangeRateList: copy }));
  };

  const checkDuplicate = (value) => {
    const found = activeItem.combinationCurrencies.filter((val) => val?.currency?.value === value?.value);
    if (found.length !== 0) {
      toasterMsg(found.map((val) => val?.currency?.value).join(', ') + t('TOASTER.DUPLICATE_CURRENCY'), t('TOASTER.DANGER'));
      return true;
    }
    return false;
  };

  const clearComboError = (innerIdx, field) => {
    setState((prevState) => {
      const combinationErrors = [...(prevState.error?.combinationErrors || [])];
      if (combinationErrors[innerIdx]) {
        combinationErrors[innerIdx] = { ...combinationErrors[innerIdx], [field]: '' };
      }
      return { ...prevState, error: { ...prevState.error, combinationErrors } };
    });
  };

  const handleCurrency = (value, innerIdx) => {
    if (!checkDuplicate(value)) {
      const copy = cloneDeep(exchangeRateList);
      const idx = copy.findIndex((item) => item.defaultCurrency?.value === selectedDefaultCurrency?.value);
      if (idx !== -1) copy[idx].combinationCurrencies[innerIdx].currency = value;
      setState((prevState) => ({ ...prevState, exchangeRateList: copy }));
      clearComboError(innerIdx, 'currency');
    }
  };

  const handleCombinationValue = (value, innerIdx) => {
    const copy = cloneDeep(exchangeRateList);
    const idx = copy.findIndex((item) => item.defaultCurrency?.value === selectedDefaultCurrency?.value);
    if (idx !== -1) copy[idx].combinationCurrencies[innerIdx].value = value;
    setState((prevState) => ({ ...prevState, exchangeRateList: copy }));
    clearComboError(innerIdx, 'value');
  };

  const checkValidation = () => {
    let isValid = true;
    const errors = {};
    if (!state?.seasonId) {
      errors.seasonId = t('VALIDATION.REQUIRED_SEASON');
      isValid = false;
    }
    if (!state?.year) {
      errors.year = t('VALIDATION.REQUIRED_YEAR');
      isValid = false;
    }
    if (selectedDefaultCurrency && checkArray(activeItem.combinationCurrencies)) {
      const combinationErrors = activeItem.combinationCurrencies.map((combo) => {
        const comboErr = {};
        if (!combo.currency) {
          comboErr.currency = t('VALIDATION.REQUIRED_CURRENCY');
          isValid = false;
        } else if (combo.currency?.value === selectedDefaultCurrency?.value) {
          comboErr.currency = t('VALIDATION.SAME_AS_DEFAULT_CURRENCY');
          isValid = false;
        }
        if (!combo.value || combo.value.trim() === '') {
          comboErr.value = t('VALIDATION.REQUIRED_VALUE');
          isValid = false;
        }
        return comboErr;
      });
      if (combinationErrors.some((e) => Object.keys(e).length > 0)) {
        errors.combinationErrors = combinationErrors;
      }
    }
    setState((prevState) => ({ ...prevState, error: errors }));
    return isValid;
  };

  const onSave = async () => {
    if (checkValidation()) {
      setState((prevState) => ({ ...prevState, isSaved: true }));
      const obj = {
        _id: exchangeRateId,
        seasonId: state?.seasonId?._id,
        year: state?.year?.name,
        exchangeRateList: state.exchangeRateList.map((item) => ({
          defaultCurrency: item.defaultCurrency?.value,
          defaultCurrencyValue: item.defaultCurrencyValue,
          combinationCurrencies: item.combinationCurrencies.map((rate) => ({
            currency: rate.currency?.value,
            value: rate.value,
          })),
        })),
      };
      const res = await dispatch(saveExhangeRate(obj));
      if (res) {
        await dispatch(
          fetchExchangeRate({
            seasonId: state?.seasonId?._id,
            year: state?.year?.name,
          }),
        );
        setState((prevState) => ({ ...prevState, isSaved: false }));
      }
    }
  };

  return (
    <Accordion className="mb-4 master-accordian custom-attribute-master">
      <Card>
        <Card.Header>
          <div className="title-container">
            <p>{t('EXCHANGE_RATE.TITLE')}</p>
          </div>
          <div className="action-title-btns single-btn">
            <OverlayTrigger placement="auto" delay={{ show: 250, hide: 400 }} overlay={renderTooltipOpenCloseCustomAttr}>
              <button type="button" onClick={toggleComp}>
                {btnCall()}
              </button>
            </OverlayTrigger>
          </div>
        </Card.Header>
        {!buttonDownCustomAttr ? (
          loader ? (
            <Loader />
          ) : (
            <Card.Body>
              <Container fluid className="pt-3">
                <Row className="form-row">
                  <Col lg={6}>
                    <FormGroup>
                      <Select
                        options={seasonList}
                        maxMenuHeight={110}
                        placeholder={t('EXCHANGE_RATE.SELECT_SEASON')}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option._id}`}
                        name="season"
                        value={seasonId}
                        styles={SelectBoxStylesForProductAttribute}
                        onChange={handleSeasonChange}
                      />
                      {error && <span style={{ color: 'red' }}>{error?.seasonId}</span>}
                    </FormGroup>
                  </Col>
                  <Col lg={6}>
                    <FormGroup>
                      <Select
                        components={{ Option: CurrentYearOption }}
                        options={yearOpionsArr}
                        maxMenuHeight={110}
                        placeholder={t('EXCHANGE_RATE.SELECT_YEAR')}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option._id}`}
                        name="year"
                        value={year}
                        styles={SelectBoxStylesForProductAttribute}
                        onChange={handleYearChange}
                      />
                      {error && <span style={{ color: 'red' }}>{error?.year}</span>}
                    </FormGroup>
                  </Col>
                </Row>
              </Container>
              <PerfectScrollbar>
                <Container fluid>
                  {/* Single defaultCurrency selector — drives which combination list is shown */}
                  <Row className="form-row">
                    <Col lg={6}>
                      <FormGroup>
                        <Select
                          menuPosition="fixed"
                          menuPlacement="auto"
                          maxMenuHeight={110}
                          options={currencyOptionsArr}
                          placeholder={t('EXCHANGE_RATE.SELECT_TYPE')}
                          getOptionLabel={(option) => `${option.value}`}
                          getOptionValue={(option) => `${option.label}`}
                          name="defaultCurrency"
                          value={selectedDefaultCurrency}
                          styles={SelectBoxStylesForProductAttribute}
                          onChange={handleDefaultCurrency}
                          isDisabled={!MasterAddEditAccess}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6}>
                      <FormGroup>
                        <input value={activeItem.defaultCurrencyValue} type="text" className="form-control text-bordered" readOnly />
                      </FormGroup>
                    </Col>
                  </Row>
                  {/* Combination currencies for the selected defaultCurrency */}
                  {selectedDefaultCurrency &&
                    checkArray(activeItem.combinationCurrencies) &&
                    activeItem.combinationCurrencies.map((combo, innerIdx) => (
                      <Row className="form-row" key={innerIdx}>
                        <Col lg={6}>
                          <FormGroup>
                            <Select
                              menuPosition="fixed"
                              menuPlacement="top"
                              maxMenuHeight={110}
                              options={currencyOptionsArr.filter((opt) => opt.value !== selectedDefaultCurrency?.value)}
                              placeholder={sMaster('EXCHANGE_RATE.SELECT_CURRENCY')}
                              getOptionLabel={(option) => `${option.value}`}
                              getOptionValue={(option) => `${option.label}`}
                              name="type"
                              value={combo.currency}
                              styles={SelectBoxStylesForProductAttribute}
                              onChange={(e) => handleCurrency(e, innerIdx)}
                              menuShouldBlockScroll
                              isDisabled={!MasterAddEditAccess}
                            />
                            {error?.combinationErrors?.[innerIdx]?.currency && (
                              <span style={{ color: 'red', fontSize: '12px' }}>{error.combinationErrors[innerIdx].currency}</span>
                            )}
                          </FormGroup>
                        </Col>
                        <Col lg={6}>
                          <FormGroup className="d-flex form-group flex-wrap">
                            <div className="d-flex w-100">
                              <input
                                value={combo.value}
                                type="text"
                                className="form-control text-bordered"
                                disabled={!MasterAddEditAccess}
                                onChange={(e) => {
                                  if (checkIsNumber(e.target.value)) handleCombinationValue(e.target.value, innerIdx);
                                }}
                                placeholder={sMaster('EXCHANGE_RATE.ADD_CURRENCY_VALUE')}
                              />
                              {MasterAddEditAccess && activeItem.combinationCurrencies.length - 1 === innerIdx && (
                                <button className="btn btn-outline btn-38 mr-0" onClick={addCombinationCurrency}>
                                  <span className="icon-add f-20" />
                                </button>
                              )}
                              {activeItem.combinationCurrencies.length > 1 && (
                                <button className="btn btn-outline btn-38 mr-0" onClick={() => removeCombinationCurrency(innerIdx)}>
                                  <span className="icon-delete f-20" />
                                </button>
                              )}
                            </div>
                            {error?.combinationErrors?.[innerIdx]?.value && (
                              <span style={{ color: 'red', fontSize: '12px', width: '100%' }}>{error.combinationErrors[innerIdx].value}</span>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    ))}
                </Container>
              </PerfectScrollbar>
              <div className="container pb-3">
                {MasterAddEditAccess && (
                  <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                    <button aria-label="Save" type="button" className="btn btn-page-filter btn-white m-0" onClick={onSave}>
                      {isSaved ? (
                        <Spinner animation="border" />
                      ) : (
                        <>
                          <span className="icon-Save c-button-align f-20 mr-2" />
                          {t('EXCHANGE_RATE.SAVE')}
                        </>
                      )}
                    </button>
                  </CustomTooltip>
                )}
              </div>
            </Card.Body>
          )
        ) : null}
      </Card>
    </Accordion>
  );
}
export default ExchangeRate;
